import React, { useEffect, useState } from "react";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import LoadingState from "../shared/LoadingState";
import ErrorState from "../shared/ErrorState";

/**
 * Fetch followed hashtags for a given profile and follow status
 */
async function fetchHashtags(profileId, followStatus, limit = 100) {
  const url = `/match_feed/api/profiles/${profileId}/followed_hashtag_searches?follow_status=${followStatus}&limit=${limit}`;
  const response = await fetch(url, {
    headers: new Headers(authenticatedHeadersHash()),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${followStatus} hashtags`);
  }

  const result = await response.json();
  return { data: result.data || [], total: result.total || 0 };
}

/**
 * HashtagRow - Single hashtag display row
 */
function HashtagRow({ hashtag }) {
  return (
    <tr>
      <td>
        <span className="text-primary">{hashtag.hashtag_name}</span>
      </td>
      <td className="text-end text-muted">{hashtag.hashtag_usage_count?.toLocaleString() || 0} uses</td>
      {hashtag.force_followed_at && (
        <td>
          <span className="badge bg-info">Force Followed</span>
        </td>
      )}
    </tr>
  );
}

/**
 * HashtagSection - Collapsible section for a category of hashtags
 */
function HashtagSection({ title, icon, hashtags, total, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="card mb-3">
      <div
        className="card-header d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer" }}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => e.key === "Enter" && setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
      >
        <h6 className="mb-0">
          <i className={`fa ${icon} me-2`} />
          {title}
          <span className="badge bg-secondary ms-2">{total}</span>
        </h6>
        <i className={`fa fa-chevron-${isOpen ? "up" : "down"}`} />
      </div>
      {isOpen && (
        <div className="card-body p-0">
          {hashtags.length === 0 ? (
            <div className="text-center text-muted p-4">No hashtags in this category</div>
          ) : (
            <table className="table table-sm table-hover mb-0">
              <tbody>
                {hashtags.map((hashtag) => (
                  <HashtagRow key={hashtag.hashtag_id} hashtag={hashtag} />
                ))}
              </tbody>
            </table>
          )}
          {total > hashtags.length && (
            <div className="text-center text-muted py-2 border-top">
              Showing {hashtags.length} of {total} hashtags
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Card view for Overview page - shows summary counts and top hashtags
 */
function FollowingCardView({ profileId, userSlug }) {
  const [data, setData] = useState({ following: [], muted: [], blocked: [] });
  const [totals, setTotals] = useState({ following: 0, muted: 0, blocked: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [followingRes, mutedRes, blockedRes] = await Promise.all([
          fetchHashtags(profileId, "follow", 5),
          fetchHashtags(profileId, "mute", 0),
          fetchHashtags(profileId, "block", 0),
        ]);

        setData({
          following: followingRes.data,
          muted: [],
          blocked: [],
        });
        setTotals({
          following: followingRes.total,
          muted: mutedRes.total,
          blocked: blockedRes.total,
        });
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchAll();
  }, [profileId]);

  if (loading) return <LoadingState compact label="Loading followed hashtags..." />;
  if (error) return <ErrorState message={error} title="Unable to Load Hashtags" compact />;

  const totalAll = totals.following + totals.muted + totals.blocked;

  return (
    <div className="card mb-4 border-primary" style={{ borderLeftWidth: "4px" }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <h5 className="card-title">
              <i className="fa fa-hashtag text-primary me-2" />
              Followed Hashtags
            </h5>
            <div className="row mt-3">
              <div className="col-auto">
                <small className="text-muted d-block">Following</small>
                <span className="fs-5 fw-semibold">{totals.following}</span>
              </div>
              <div className="col-auto">
                <small className="text-muted d-block">Muted</small>
                <span className="fs-5 fw-semibold">{totals.muted}</span>
              </div>
              <div className="col-auto">
                <small className="text-muted d-block">Blocked</small>
                <span className="fs-5 fw-semibold">{totals.blocked}</span>
              </div>
            </div>
            {data.following.length > 0 && (
              <div className="mt-3">
                <small className="text-muted">Top followed:</small>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {data.following.slice(0, 5).map((h) => (
                    <span key={h.hashtag_id} className="badge bg-light text-dark">
                      {h.hashtag_name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {totalAll === 0 && <p className="text-muted mb-0 mt-2">This user is not following any hashtags.</p>}
          </div>
          <a href={`/admin/users/${userSlug}/following`} className="btn btn-outline-primary btn-sm">
            Details <i className="fa fa-arrow-right ms-1" />
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * Full view for Following tab - shows all hashtags in collapsible sections
 */
function FollowingFullView({ profileId }) {
  const [data, setData] = useState({ following: [], muted: [], blocked: [] });
  const [totals, setTotals] = useState({ following: 0, muted: 0, blocked: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [followingRes, mutedRes, blockedRes] = await Promise.all([
          fetchHashtags(profileId, "follow", 500),
          fetchHashtags(profileId, "mute", 500),
          fetchHashtags(profileId, "block", 500),
        ]);

        setData({
          following: followingRes.data,
          muted: mutedRes.data,
          blocked: blockedRes.data,
        });
        setTotals({
          following: followingRes.total,
          muted: mutedRes.total,
          blocked: blockedRes.total,
        });
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchAll();
  }, [profileId]);

  if (loading) return <LoadingState label="Loading followed hashtags..." />;
  if (error) return <ErrorState message={error} title="Unable to Load Hashtags" />;

  const totalAll = totals.following + totals.muted + totals.blocked;

  if (totalAll === 0) {
    return (
      <div className="card">
        <div className="card-body text-center text-muted py-5">
          <i className="fa fa-hashtag fa-3x mb-3" />
          <h5>No Followed Hashtags</h5>
          <p className="mb-0">This user has not followed, muted, or blocked any hashtags yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="alert alert-info mb-4">
        <i className="fa fa-info-circle me-2" />
        This is a read-only view. To modify followed hashtags, use the <strong>Impersonate</strong> feature and edit
        from the user&apos;s perspective.
      </div>

      <HashtagSection
        title="Following"
        icon="fa-heart"
        hashtags={data.following}
        total={totals.following}
        defaultOpen
      />

      <HashtagSection title="Muted" icon="fa-volume-mute" hashtags={data.muted} total={totals.muted} />

      <HashtagSection title="Blocked" icon="fa-ban" hashtags={data.blocked} total={totals.blocked} />
    </div>
  );
}

/**
 * AdminUserFollowing - Shows hashtags a user follows, mutes, or blocks
 *
 * Supports two modes:
 * - mode="card": Compact summary card with counts and top hashtags (for overview page)
 * - mode="full": Complete view with collapsible sections (default, for Following tab)
 *
 * @param {string} profileId - Profile ID to fetch data for
 * @param {string} userSlug - User slug for generating links (required for card mode)
 * @param {string} mode - "card" or "full" (default: "full")
 */
function AdminUserFollowing({ profileId, userSlug, mode = "full" }) {
  if (mode === "card") {
    return <FollowingCardView profileId={profileId} userSlug={userSlug} />;
  }

  return <FollowingFullView profileId={profileId} />;
}

export default AdminUserFollowing;
